<template>
  <loading-overlay v-if="loading" class="animated fadeIn" />
  <div class="animated fadeIn" v-else>
    <basic-heading
      :title="`Receiving ${project.getCustomer().name}`"
      class="print-hidden mb-3"
    >
      <template #right-content>
        <header-button-group :button_config="button_config" />
      </template>
    </basic-heading>
    <div class="print-hidden">
      <div v-if="scanning">
        <b-card>
          <b-alert v-if="scan_error" variant="warning" show>{{
            scan_error
          }}</b-alert>
          <b-button
            v-for="(scan_item, index) in scan_list"
            :key="index"
            class="bg-transparent text-left"
            block
          >
            <b-badge
              pill
              :variant="scan_item.status === 'Received' ? 'success' : 'info'"
              class="mr-1"
              >{{ scan_item.status }}</b-badge
            >
            {{ scan_item.formatted_pid }}
            {{ scan_item.sample ? scan_item.sample.receiving_id : null }}
          </b-button>
          <b-button class="bg-transparent" block>
            <i class="fa fa-spin fa-spinner" /> Waiting for your next scan...
            {{ scanned ? `(Last Scan: ${scanned})` : null }}
          </b-button>
        </b-card>
      </div>
      <div v-else>
        <b-button variant="primary" block size="lg">
          <b>Focus on this page to begin scanning...</b>
        </b-button>
        <div class="my-1" />
      </div>
    </div>
    <b-row class="pr-3 pt-3 mb-3" align-h="end">
      <b-col md="1.5" class="ml-2">
        <b-button
          variant="success"
          block
          @click="receiveSamples(sample_collection)"
        >
          <i class="fa fa-check-square" /> Receive Project
        </b-button>
      </b-col>
    </b-row>
    <template>
      <div v-for="(submission, index) in submissions" :key="index">
        <b-row class="display-3 font-xl mb-3 pr-3">
          <b-col> Submission: {{ submission.title }} </b-col>
          <b-col v-if="submissions.length > 1" md="1.5" class="ml-2">
            <b-button
              variant="success"
              block
              @click="receiveSamples(submission.sample_collection)"
            >
              <i class="fa fa-check-square" /> Receive Submission
            </b-button>
          </b-col>
        </b-row>
        <sample-receiving-labels
          :samples="submission.sample_collection"
          :receive="true"
          @receiveSample="(sample) => onBarcodeScanned(sample.publicNumber())"
        />
        <hr class="mt-0" />
      </div>
    </template>
    <b-row class="pr-3 mb-3" align-h="end">
      <b-col md="1.5" class="ml-2">
        <b-button variant="primary" block @click="backToReceive">
          <i class="fa fa-list" /> Back to Receive
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import BasicHeading from "@/components/shared/BasicHeading";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import ProjectModel from "@/models/lab/ProjectModel";
import SampleCollection from "@/collections/lab/SampleCollection";
import SampleReceiveTable from "@/components/lab/sample/SampleReceiveTable";
import LoadingOverlay from "@/components/shared/LoadingOverlay";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import SampleReceivingLabels from "@/components/lab/sample/SampleReceivingLabels";

export default {
  name: "ReceiveDetail",
  components: {
    SampleReceivingLabels,
    LoadingOverlay,
    SampleReceiveTable,
    BasicHeading,
    HeaderButtonGroup,
  },
  mixins: [ToastNotifications],
  data() {
    return {
      loading: true,
      project: null,
      project_id: this.$route.params.project_id || null,
      sample_collection: new SampleCollection(),
      submissions: null,
      scanning: false,
      scan_error: null,
      scanned: null,
      scanned_sanitized: null,
      scan_list: [],
      receiving: false,
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-list",
            linkto: "/samples/receive",
            title: "To All receiving",
          },
          {
            icon: "fa-box",
            linkto: `/projects/${this.project_id}`,
            title: "To project",
          },
        ],
      };
    },
  },
  methods: {
    receiveSamples(sample_collection) {
      const collection_for_receive = new SampleCollection();

      sample_collection.models.forEach((sample) => {
        if (!sample.receiving_id) {
          collection_for_receive.add(sample);
        }
      });
      collection_for_receive.models.forEach((sample) => {
        this.onBarcodeScanned(sample.publicNumber());
      });
    },
    backToReceive() {
      this.$router.push("/samples/receive");
    },
    markedReceived() {
      this.$refs.sample_receive_table.refresh();
    },
    onBarcodeScanned(barcode) {
      this.scanned = barcode;
      const matches = barcode.match(/SA-(\d{6})/g);

      if (!matches || !matches.length) {
        this.scan_error = "Scan does not match the pattern SA-XXXXXX.";
        return false;
      }

      this.addScanItem({
        code: barcode,
        pid: barcode.match(/\d+/g).map(Number),
        formatted_pid:
          "SA-" + barcode.match(/\d+/g).map(Number).toString().padStart(6, "0"),
        sample: null,
        status: null,
      });
    },
    clickReceived(sample) {
      this.addScanItem({
        code: null,
        pid: +sample.get("pid"),
        formatted_pid: sample.publicNumber(),
        sample: sample,
        status: null,
      });
    },
    addScanItem(item) {
      this.scan_error = null;
      this.scan_list.forEach((scan_item) => {
        if (
          (item.code && scan_item.code === item.code) ||
          scan_item.pid === item.pid
        ) {
          this.scan_error = "This item has already been scanned";
        }
      });

      if (this.scan_error) {
        return false;
      }

      item.status = "Receiving";

      if (!item.sample) {
        const collection = new SampleCollection();
        collection.getFilter().filterEquals("pid", item.pid);
        collection.fetch().then(() => {
          const sample = collection.models[0];

          if (sample) {
            item.sample = sample;
            this.updateSampleStatus(item.sample).then(() => {
              item.status = "Received";
            });
          }
        });
      } else {
        this.updateSampleStatus(item.sample).then(() => {
          item.status = "Received";
        });
      }
      this.scan_list.push(item);

      return item;
    },
    updateSampleStatus(sample) {
      return new Promise((resolve, reject) => {
        const log = sample.getCurrentSampleStatusLog();

        if (
          log &&
          log.getSampleStatus() &&
          log.getSampleStatus().status === "Sent"
        ) {
          sample.updateStatus("Received").then(
            (log) => {
              sample.set("current_sample_status_log", log);

              if (log.sample && log.sample.receiving_id) {
                this.quickSuccessToast(
                  "Marked received",
                  "Sample was successfully marked received"
                );
                sample.set("receiving_id", log.sample.receiving_id);

                const models = this.sample_collection.models;
                let found = null;

                models.forEach((model, index) => {
                  if (model.id === sample.id) {
                    found = index;
                  }
                });

                models.splice(found, 1, sample);
                this.sample_collection.replace(models);
                this.print(sample);
              }
              resolve(log);
            },
            () => {
              this.quickWarningToast(
                "Could not mark received",
                "Sample could not be marked received"
              );
            }
          );
        } else {
          return reject();
        }
      });
    },
    print(sample) {
      this.quickInfoToast(
        "Printing Sample",
        `Sample ${sample.get("receiving_id")} will be printed shortly...`
      );
    },
  },
  created() {
    this.loading = true;

    setInterval(() => {
      if (document.hasFocus()) {
        this.scanning = true;
        this.$barcodeScanner.init(this.onBarcodeScanned);
      } else {
        this.scanning = false;
        this.$barcodeScanner.destroy();
      }
    }, 300);

    const project = new ProjectModel({ id: this.project_id });

    project
      .fetch({
        params: {
          connection: [
            "customer",
            "submissions",
            "submissions.samples",
            "submissions.samples.submission.project",
            "submissions.samples.currentSampleStatusLog",
            "submissions.samples.currentSampleStatusLog.sampleStatus",
          ],
        },
      })
      .then(() => {
        const submissions = project.getSubmissions();

        this.submissions = project.getSubmissions().models.map((submission) => {
          const sample_collection = new SampleCollection();

          submission.samples.forEach((sample) => {
            sample_collection.add(sample);
          });
          return {
            title: submission.publicNumber(),
            sample_collection,
          };
        });

        const sample_collection = new SampleCollection();

        submissions.models.forEach((submission) => {
          const samples = submission.getSamples();

          samples.models.forEach((sample) => {
            sample_collection.add(sample);
          });
        });

        this.project = project;
        this.sample_collection = sample_collection;
        this.loading = false;
      });
  },
  destroyed() {
    this.$barcodeScanner.destroy();
  },
};
</script>
